/* global REACT_APP_PUBLIC_SENTRY_ENVIRONMENT */

// Rastro auditable de las conversiones de registro. Existe para que CUALQUIER corrida de QA se
// pueda auditar leyendo UNA estructura, en vez de reconstruirla desde `performance` sabiendo de
// memoria que el id de Meta viaja como `eid=` y el de GA4 como `ep.meta_event_id`.
//
// Por qué se agregó: la validación del 2026-08-31 encontró dos defectos que el navegador NO
// muestra a simple vista — (1) el `Purchase` salió DOS veces por un doble clic, y (2) el id que
// creíamos mandar a GA4 nunca llegaba porque `event_id` es nombre reservado. Los dos se
// diagnosticaron leyendo requests crudos. Con este rastro, ambos son evidentes de un vistazo: el
// duplicado son dos filas, y el cruce roto es un `eventId` que no coincide entre sinks.
//
// **No corre en producción.** El gate es el mismo criterio que ya usa `ga4.isDev()`: solo
// `vite.config.prod.ts` pone `production`. Es un instrumento de QA, no telemetría.
export type ConversionSink = 'meta' | 'ga4';

export type ConversionRecord = {
  seq: number;
  sink: ConversionSink;
  event: string;
  // Id de deduplicación de Meta. `null` = el pixel no emitió (típico: AdBlock bloqueó
  // `fbevents.js`). Que la fila EXISTA con `eventId: null` es justo lo que se quiere ver: la
  // conversión se intentó y GA4 sí la recibió, aunque Meta no.
  eventId: string | null;
  params: Record<string, unknown>;
};

export type ConversionAuditSummary = {
  total: number;
  porEvento: Record<string, number>;
  duplicados: string[];
  // `sin-cruce` cuando falta alguno de los dos lados: declarar `ok` sin datos sería el falso
  // verde de siempre — una aserción por ausencia que pasa igual si el instrumento no midió.
  cruceMetaGa4: 'ok' | 'sin-cruce' | 'ids-distintos';
  registros: ConversionRecord[];
};

declare global {
  interface Window {
    __plickConversions?: ConversionRecord[];
    __plickConversionAudit?: () => ConversionAuditSummary;
  }
}

// Tope defensivo: un bug de reentrada no debe convertir el rastro en una fuga de memoria. 50 es
// muy superior a lo que produce cualquier registro real (2 filas) y sigue mostrando un duplicado.
const MAX_RECORDS = 50;

const isAuditEnabled = (): boolean => {
  try {
    return REACT_APP_PUBLIC_SENTRY_ENVIRONMENT !== 'production';
  } catch {
    // Sin la constante definida (tests, SSR) se asume NO producción: el riesgo de auditar de más
    // en un entorno de prueba es nulo; el de auditar en prod, no.
    return true;
  }
};

export const recordConversion = (
  sink: ConversionSink,
  event: string,
  eventId: string | null,
  params: Record<string, unknown> = {},
): void => {
  if (!isAuditEnabled() || typeof window === 'undefined') return;
  try {
    const log = window.__plickConversions ?? [];
    if (log.length >= MAX_RECORDS) return;
    log.push({ seq: log.length + 1, sink, event, eventId, params });
    window.__plickConversions = log;
  } catch {
    // noop — auditar jamás debe romper la conversión que audita
  }
};

// Lectura para QA. Se expone como función y no solo como array para que la consola pueda pedir el
// veredicto sin conocer la forma interna ni los nombres de parámetro de cada plataforma.
export const conversionAuditSummary = (): ConversionAuditSummary => {
  const registros = (typeof window !== 'undefined' && window.__plickConversions) || [];
  const porEvento: Record<string, number> = {};
  registros.forEach((r) => {
    const k = `${r.sink}:${r.event}`;
    porEvento[k] = (porEvento[k] ?? 0) + 1;
  });

  // Un registro sano emite CADA evento una sola vez por sink. Dos filas iguales = doble envío,
  // que fue exactamente el defecto del 31-ago.
  const duplicados = Object.keys(porEvento).filter((k) => porEvento[k] > 1);

  const metaIds = registros.filter((r) => r.sink === 'meta').map((r) => r.eventId);
  const ga4Ids = registros.filter((r) => r.sink === 'ga4').map((r) => r.eventId);
  let cruceMetaGa4: 'ok' | 'sin-cruce' | 'ids-distintos' = 'sin-cruce';
  if (metaIds.length && ga4Ids.length) {
    cruceMetaGa4 = ga4Ids.every((id) => id !== null && metaIds.includes(id)) ? 'ok' : 'ids-distintos';
  }

  return { total: registros.length, porEvento, duplicados, cruceMetaGa4, registros };
};

// Publica el lector en `window` para que QA (humano o agente) corra UNA línea en la consola:
//
//     window.__plickConversionAudit()
//
// y obtenga el veredicto — cuántos eventos por sink, si hubo duplicados, y si el id cruza entre
// Meta y GA4 — sin tener que saber que en Meta el id viaja como `eid=` y en GA4 como
// `ep.meta_event_id`. Igual que el resto del módulo, es no-op en producción.
export const exposeConversionAudit = (): void => {
  if (!isAuditEnabled() || typeof window === 'undefined') return;
  try {
    window.__plickConversionAudit = conversionAuditSummary;
  } catch {
    // noop
  }
};
